import { useState } from 'react'
import { apiFetch } from '../lib/api'

const SEND_PHONE_OTP = String(import.meta.env.VITE_SEND_PHONE_OTP || '').toLowerCase() === 'true'

/**
 * Modal shown when user hasn't saved a phone number yet.
 * Prompts for WhatsApp number, sends OTP via Meta API, verifies it.
 * On success calls onVerified(phone) so the parent can proceed.
 */
export default function PhoneOtpModal({ onVerified, onClose }) {
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [step, setStep] = useState('phone') // 'phone' | 'otp'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSendOtp(e) {
    e.preventDefault()
    setError('')
    if (!phone.trim()) { setError('Please enter your WhatsApp number.'); return }
    setLoading(true)
    try {
      if (!SEND_PHONE_OTP) {
        const res = await apiFetch('/users/me/phone', {
          method: 'POST',
          body: JSON.stringify({ phone: phone.trim() }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) { setError(data.message || 'Failed to save phone number.'); return }
        onVerified(data.phone || phone.trim())
        return
      }

      const res = await apiFetch('/users/me/send-phone-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setError(data.message || 'Failed to send OTP.'); return }
      setStep('otp')
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault()
    setError('')
    if (!otp.trim()) { setError('Please enter the OTP.'); return }
    setLoading(true)
    try {
      const res = await apiFetch('/users/me/verify-phone-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim(), otp: otp.trim() }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.message || 'Invalid OTP.'); return }
      onVerified(data.phone)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="title-font text-xl font-bold text-gray-900">Verify WhatsApp Number</h2>
          <button onClick={onClose} className="rounded-full p-1 text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Close">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {step === 'phone' ? (
          <form onSubmit={handleSendOtp} noValidate>
            <p className="mb-4 text-sm text-gray-600">
              {SEND_PHONE_OTP
                ? "To send an eat-request, we need your WhatsApp number. We'll send a one-time code to verify it."
                : 'To continue, please add your WhatsApp number.'}
            </p>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              WhatsApp Number (with country code)
            </label>
            <input
              type="tel"
              placeholder="+91 98765 43210"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full rounded-xl border border-orange-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              autoFocus
            />
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-60"
            >
              {loading ? (SEND_PHONE_OTP ? 'Sending…' : 'Saving…') : (SEND_PHONE_OTP ? 'Send OTP on WhatsApp' : 'Save Number')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} noValidate>
            <p className="mb-4 text-sm text-gray-600">
              Enter the 6-digit code sent to <span className="font-semibold text-gray-800">{phone}</span> on WhatsApp.
            </p>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              OTP Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              placeholder="123456"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-xl border border-orange-200 px-4 py-3 text-center text-2xl font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-orange-400"
              autoFocus
            />
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="mt-4 w-full rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-60"
            >
              {loading ? 'Verifying…' : 'Verify & Continue'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('phone'); setOtp(''); setError('') }}
              className="mt-2 w-full rounded-2xl py-2 text-sm text-gray-500 hover:text-gray-800"
            >
              ← Change number
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
